import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { cn } from '@heidi/utils';
import { AlertCircle, CheckCircle2, Loader2, Mail } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

interface EmailPatientEvidenceBlockProps {
  status: 'sending' | 'completed' | 'error';
  patientEmail: string;
  error?: string;
  needsEmail?: boolean;
  needsPdf?: boolean;
  className?: string;
}

/**
 * EmailPatientEvidenceBlock - Displays email sending status in the chat timeline
 *
 * Visual style aligned with ArtefactBlock for consistency across tool call blocks.
 *
 * Visual states:
 * - SENDING: Spinner with "Sending email..." and masked email
 * - COMPLETED: Success icon with confirmation and masked email
 * - ERROR: Error icon with error message and helpful hints
 */
export const EmailPatientEvidenceBlock = ({
  status,
  patientEmail,
  error,
  needsEmail,
  needsPdf,
  className,
}: EmailPatientEvidenceBlockProps): JSX.Element => {
  const { t } = useTranslations();
  const intl = useIntl();

  // Mask email for display (show first 2 chars and domain)
  const maskedEmail = (() => {
    if (!patientEmail || !patientEmail.includes('@')) return patientEmail;
    const [local, domain] = patientEmail.split('@');
    if (local && local.length > 2) {
      return `${local.slice(0, 2)}${'*'.repeat(local.length - 2)}@${domain}`;
    }
    return patientEmail;
  })();

  // === SENDING STATE ===
  if (status === 'sending') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-border bg-surface-accent p-4',
          'transition-all duration-200',
          className
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2 className="truncate font-medium text-text-primary">
            {t('chat.sendingEmail')}
          </TypographyP2>
          <div className="flex items-center gap-1.5">
            <Loader2 className="size-3 animate-spin text-text-tertiary" />
            <TypographyCaption className="truncate text-text-tertiary">
              {maskedEmail}
            </TypographyCaption>
          </div>
        </div>
      </div>
    );
  }

  // === ERROR STATE ===
  if (status === 'error') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-validation-destructive-border bg-validation-destructive-muted p-4',
          'transition-all duration-200',
          className
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2 className="font-medium text-text-primary">
            {t('chat.emailFailed')}
          </TypographyP2>
          <div className="flex items-center gap-1.5">
            <AlertCircle className="size-3 text-validation-destructive" />
            <TypographyCaption className="break-words text-validation-destructive">
              {error ||
                intl.formatMessage({
                  defaultMessage: 'Something went wrong',
                  id: 'JqiqNj0Ooi',
                })}
            </TypographyCaption>
          </div>
          {needsEmail && (
            <TypographyCaption className="text-text-tertiary">
              {t('chat.emailNeeded')}
            </TypographyCaption>
          )}
          {needsPdf && (
            <TypographyCaption className="text-text-tertiary">
              {t('chat.pdfNeeded')}
            </TypographyCaption>
          )}
        </div>
      </div>
    );
  }

  // === COMPLETED STATE ===
  return (
    <div
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border border-validation-success-border bg-validation-success-muted p-4',
        'transition-all duration-200',
        className
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <CheckCircle2 className="size-3.5 text-validation-success" />
          <TypographyP2 className="truncate font-medium text-text-primary">
            {t('chat.emailSent')}
          </TypographyP2>
        </div>
        <div className="flex items-center gap-1.5">
          <Mail className="size-3 text-text-tertiary" />
          <TypographyCaption className="truncate text-text-tertiary">
            {maskedEmail}
          </TypographyCaption>
        </div>
        <TypographyCaption className="text-validation-success">
          <FormattedMessage
            defaultMessage="The patient will receive their health summary PDF as an attachment."
            id="rNtnIdQ0JK"
            description="Caption under the email-sent confirmation in Evidence chat"
          />
        </TypographyCaption>
      </div>
    </div>
  );
};
